import React, { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import Menu from '@mui/material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LanguageIcon from '@mui/icons-material/Language';

import PhoneNumberService, { CountryOption } from '../../../../Services/PhoneNumberService';

import { CountryCodePickerProps } from './CountryCodePicker.types';
import {
  StyledTrigger, StyledTriggerLabel, StyledSearchContainer, StyledSearchField,
  StyledMenuItem, StyledCountryName, StyledCallingCode, StyledEmptyMessage,
  MenuPaperStyles, MenuListStyles,
} from './CountryCodePicker.theme';
import { Styles } from './CountryCodePicker.theme';

const matchesSearchTerm = (country: CountryOption, searchTerm: string) => {
  const term = searchTerm.trim().toLowerCase();

  if (!term) return true;

  return country.name.toLowerCase().includes(term)
    || country.code.toLowerCase().includes(term)
    || country.callingCode.includes(term.replace('+', ''));
};

const CountryCodePickerComponent = forwardRef<HTMLDivElement, CountryCodePickerProps>((props, ref) => {
  const { id, className, style, selectedCountry, disabled, menuZIndex, allowedCallingCodes, onCountrySelect } = props;

  const triggerRef = useRef<HTMLDivElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const allCountries = PhoneNumberService.getCountryOptions();
  const allowedKey = JSON.stringify(allowedCallingCodes ?? null);
  const countries = useMemo(
    () => PhoneNumberService.getCountryOptions(allowedCallingCodes),
    // Recalc when the tenant list contents change, not only the array reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allowedKey]
  );
  const filteredCountries = useMemo(
    () => countries.filter(country => matchesSearchTerm(country, searchTerm)),
    [countries, searchTerm]
  );

  const selectedCallingCode = useMemo(
    () => allCountries.find(country => country.code === selectedCountry)?.callingCode,
    [allCountries, selectedCountry]
  );
  const selectedCountryNameCode = useMemo(
    () => allCountries.find(country => country.code === selectedCountry)?.code,
    [allCountries, selectedCountry]
  );

  const handleOpen = useCallback(() => {
    setSearchTerm('');
    setMenuOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setMenuOpen(false);
  }, []);

  const handleSelect = useCallback((country: CountryOption) => {
    onCountrySelect?.(country.code);
    setMenuOpen(false);
  }, [onCountrySelect]);

  // The number field and the dialler both listen for key presses, so typing in
  // the search field must not bubble out of the menu.
  const handleSearchKeyDown = useCallback((event: React.KeyboardEvent) => {
    event.stopPropagation();
  }, []);

  return (
    <div id={id} ref={ref} className={className} style={style}>
      <StyledTrigger
        id={`${id}-Trigger`}
        ref={triggerRef}
        disabled={disabled}
        onClick={handleOpen}
      >
        {selectedCountryNameCode ? (
          <StyledTriggerLabel>{selectedCountryNameCode}</StyledTriggerLabel>
        ) : (
          <LanguageIcon sx={{ fontSize: 16, color: '#FFFFFF' }} />
        )}
        <ExpandMoreIcon sx={{ fontSize: 16, color: '#FFFFFF' }} />
      </StyledTrigger>
      <Menu
        id={`${id}-Menu`}
        anchorEl={triggerRef.current}
        open={menuOpen}
        onClose={handleClose}
        autoFocus={false}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={Styles.menu}
        PaperProps={{ sx: MenuPaperStyles }}
      >
        <StyledSearchContainer>
          <StyledSearchField
            id={`${id}-Search`}
            autoFocus
            fullWidth
            placeholder="Search country"
            value={searchTerm}
            onChange={event => setSearchTerm(event.target.value)}
            onKeyDown={handleSearchKeyDown}
            inputProps={{ autoComplete: 'off' }}
          />
        </StyledSearchContainer>
        {filteredCountries.map(country => (
          <StyledMenuItem
            key={country.code}
            id={`${id}-${country.code}`}
            selected={country.code === selectedCountry}
            onClick={() => handleSelect(country)}
          >
            <StyledCountryName>{country.name}</StyledCountryName>
            <StyledCallingCode>{country.callingCode}</StyledCallingCode>
          </StyledMenuItem>
        ))}
        {!filteredCountries.length && (
          <StyledEmptyMessage>No countries found</StyledEmptyMessage>
        )}
      </Menu>
    </div>
  );
});

CountryCodePickerComponent.defaultProps = {
  className: undefined,
  style: undefined,
  selectedCountry: undefined,
  disabled: false,
  menuZIndex: 1300 as any,
  allowedCallingCodes: undefined,
  onCountrySelect: () => {},
};

export default CountryCodePickerComponent;
